/**
 * Client-side setup-code canonicalization + shape check, mirroring
 * `web/functions/identity.setup_code_hash`'s canonical form. Used to validate
 * what a caregiver types before we call the backend — catches a wrong-length
 * or out-of-alphabet code early, with a clear message.
 */

/** Crockford base32 — the generated alphabet (excludes I, L, O, U). */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Uppercase, strip separators, and de-ambiguate the Crockford confusables a
 * human might type for a digit (O→0, I/L→1) — matching the backend hash input.
 * Real codes never contain O/I/L, so this only rescues a misread digit.
 */
export function canonicalizeSetupCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

/**
 * Shape check: exactly 8 characters, all within the Crockford alphabet once
 * canonicalized. Catches the common typos (wrong length, a stray non-Crockford
 * letter like a typed U) before the backend lookup.
 */
export function isSetupCodeShapeValid(raw: string): boolean {
  const c = canonicalizeSetupCode(raw);
  return c.length === 8 && [...c].every((ch) => CROCKFORD.includes(ch));
}
